import { ReactNode } from 'react';
import { TimeFilterType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { i18next } from '@frontend/i18n';

export const formatHourLabel = (hourValue: string) => {
  const hour = Number(hourValue);
  if (Number.isNaN(hour)) {
    return hourValue;
  }

  const normalizedHour = ((hour % 24) + 24) % 24;
  const meridiemHour = normalizedHour % 12 || 12;
  return `${meridiemHour} ${normalizedHour < 12 ? 'am' : 'pm'}`;
};

export const formatDailyXAxisTick = (value: string) => {
  const date = dayjs(value);
  if (!date.isValid()) {
    return value;
  }

  const currentYear = dayjs().year();
  return date.year() === currentYear ? date.format('MMM D') : date.format('MMM D, YYYY');
};

export const formatTooltipTitle = (groupName: string, selectedXAxis: TimeFilterType, count?: number): ReactNode => {
  if (selectedXAxis === TimeFilterType.DAILY) {
    const date = dayjs(groupName);
    if (!date.isValid()) {
      return groupName;
    }

    const monthAndDay = date.format('MMMM D');
    const year = date.format('YYYY');

    return (
      <>
        <strong>{monthAndDay}</strong>, {year}
      </>
    );
  }

  if (selectedXAxis === TimeFilterType.DAY_OF_WEEK) {
    const translatedDayName =
      count == 1
        ? i18next.t('{{rawName}}', { rawName: groupName, ns: 'analytics' })
        : i18next.t('{{rawName}}s', { rawName: groupName, ns: 'analytics' });
    return (
      <strong>
        {count ? `${count} ` : ''}
        {translatedDayName}
      </strong>
    );
  }

  if (selectedXAxis === TimeFilterType.HOURLY) {
    const hour = Number(groupName);
    if (Number.isNaN(hour)) {
      return groupName;
    }

    const normalizedHour = ((hour % 24) + 24) % 24;
    const timeLabel = formatHourLabel(groupName).toUpperCase();
    const translatedMorning = i18next.t('Morning', { ns: 'analytics' });
    const translatedAfternoon = i18next.t('Afternoon', { ns: 'analytics' });
    const translatedEvening = i18next.t('Evening', { ns: 'analytics' });
    const dayPeriod =
      normalizedHour < 12 ? translatedMorning : normalizedHour < 17 ? translatedAfternoon : translatedEvening;

    return (
      <strong>
        {dayPeriod} {timeLabel}
      </strong>
    );
  }

  return groupName;
};

export const transformGroupName = (rawName: string, selectedXAxis: TimeFilterType): string => {
  switch (selectedXAxis) {
    case TimeFilterType.HOURLY:
      return formatHourLabel(rawName);
    case TimeFilterType.DAY_OF_WEEK:
      return i18next.t('{{rawName}}s', { rawName, ns: 'analytics' });
    case TimeFilterType.DAILY:
      return formatDailyXAxisTick(rawName);
    default:
      return rawName;
  }
};
